import type { Product } from '../types';
import { env } from '../config/env';

export const MAX_PRODUCT_IMAGES = 10;
export const PRODUCT_IMAGE_ACCEPT = 'image/png,image/jpeg,image/webp';
export const PRODUCT_IMAGE_MAX_BYTES = 5 * 1024 * 1024;

export function resolveProductImageUrl(image: string): string {
  if (!image) return '';
  if (
    image.startsWith('http://') ||
    image.startsWith('https://') ||
    image.startsWith('blob:') ||
    image.startsWith('data:')
  ) {
    return image;
  }

  if (image.startsWith('/')) {
    const origin = env.apiOrigin || deriveApiOrigin(env.apiUrl);
    return origin ? `${origin}${image}` : image;
  }

  return image;
}

function deriveApiOrigin(url: string): string {
  if (!url) return '';
  return url.replace(/\/api\/?$/, '');
}

export function validateProductImageFiles(files: File[], currentCount = 0): string | null {
  if (currentCount + files.length > MAX_PRODUCT_IMAGES) {
    return `Ürün başına en fazla ${MAX_PRODUCT_IMAGES} görsel yüklenebilir.`;
  }

  for (const file of files) {
    if (!PRODUCT_IMAGE_ACCEPT.split(',').includes(file.type)) {
      return `${file.name}: Yalnızca PNG, JPG veya WEBP yüklenebilir.`;
    }
    if (file.size > PRODUCT_IMAGE_MAX_BYTES) {
      return `${file.name}: Dosya boyutu 5 MB'ı aşamaz.`;
    }
  }

  return null;
}

export function getUnitPrice(product: Pick<Product, 'price' | 'effectivePrice'>): number {
  return product.effectivePrice ?? product.price;
}

export function getPrimaryImage(product: Pick<Product, 'images'>): string {
  const primary = product.images[0] ?? '';
  return resolveProductImageUrl(primary);
}

export function isInStock(product: Product): boolean {
  return product.stock > 0;
}

export function canAddToCart(product: Product): boolean {
  return isInStock(product) && !product.priceLabel;
}

export function canPreOrder(product: Product): boolean {
  return !isInStock(product) && !product.priceLabel;
}

export function getMaxQuantity(product: Product, currentInCart = 0): number {
  return Math.max(0, product.stock - currentInCart);
}

export function clampToStock(product: Product, quantity: number, currentInCart = 0): number {
  const max = getMaxQuantity(product, currentInCart);
  return Math.min(Math.max(1, quantity), max || 1);
}

export function parseProductId(value: string | undefined): number | null {
  if (!value) return null;
  const id = Number(value);
  return Number.isInteger(id) && id > 0 ? id : null;
}

export function getProductUrl(slug: string): string {
  return `/urun/${slug}`;
}

export function slugify(text: string): string {
  return text
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export function getProductsByCategory(products: Product[], category: string): Product[] {
  if (category === 'Tümü') return products;
  return products.filter((p) => p.category === category);
}

export function getProductCategories(products: Product[]): string[] {
  const fromProducts = products.map((p) => p.category);
  return ['Tümü', ...Array.from(new Set(fromProducts))];
}

export function parseBenefits(value: string): string[] {
  return value
    .split(',')
    .map((b) => b.trim())
    .filter(Boolean);
}

export function formatBenefits(benefits: string[]): string {
  return benefits.join(', ');
}

export function parseSpecs(value: string): Record<string, string> {
  const specs: Record<string, string> = {};
  value
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .forEach((line) => {
      const separator = line.indexOf(':');
      if (separator === -1) return;
      const key = line.slice(0, separator).trim();
      const specValue = line.slice(separator + 1).trim();
      if (key && specValue) specs[key] = specValue;
    });
  return specs;
}

export function formatSpecs(specs?: Record<string, string>): string {
  if (!specs) return '';
  return Object.entries(specs)
    .map(([key, value]) => `${key}: ${value}`)
    .join('\n');
}

export function parseImages(value: string): string[] {
  return value
    .split('\n')
    .map((url) => url.trim())
    .filter(Boolean);
}

export function formatImages(images: string[]): string {
  return images.join('\n');
}

export function normalizeImages(images: string[]): string[] {
  const unique = images.map((url) => url.trim()).filter(Boolean);
  return Array.from(new Set(unique));
}
